/**
 * capacitorPush.js
 * Handles push-notification registration & incoming-notification events
 * for the Capacitor-wrapped Lead Portal (iOS + Android).
 *
 * Critical Android notes:
 *  1. Do NOT `await` the Capacitor plugin object — it is a native Proxy.
 *     `Promise.resolve(proxy)` calls `proxy.then` which throws
 *     "PushNotifications.then() is not implemented on android".
 *
 *  2. FCM delivers the registration token ~immediately after app start,
 *     BEFORE React has mounted. Add the 'registration' listener FIRST
 *     (synchronously, before any await), then call register().
 */

/* ─────────────────────────────────────────────────────────────────────────────
   Synchronously get the PushNotifications plugin.
   Must NOT use async/await — see note 1 above.
───────────────────────────────────────────────────────────────────────────── */
function getPushPlugin() {
  try {
    return require('@capacitor/push-notifications').PushNotifications; // eslint-disable-line
  } catch {
    return null;
  }
}

/**
 * Register the device for push notifications.
 */
export async function registerPushNotifications(leadToken, apiBaseUrl, onForegroundNotification) {
  // No-op on web
  if (!window.Capacitor || !window.Capacitor.isNativePlatform()) {
    console.log('[Push] Not a native platform — skipping push setup');
    return;
  }

  const platform = window.Capacitor.getPlatform();
  console.log('[Push] Starting registration on platform:', platform);

  // Get plugin synchronously — no await
  const Push = getPushPlugin();
  if (!Push) {
    console.warn('[Push] @capacitor/push-notifications not available');
    return;
  }

  // ── Add ALL listeners FIRST (synchronously) before any await ──────────────
  // FCM may deliver the token immediately on app start. If we await anything
  // before adding this listener, we miss the token.

  Push.addListener('registration', async ({ value: deviceToken }) => {
    console.log('[Push] ✅ FCM token received:', deviceToken?.substring(0, 20) + '...');
    await savePushTokenToServer(deviceToken, leadToken, apiBaseUrl);
  });

  Push.addListener('registrationError', (err) => {
    console.error('[Push] ❌ Registration error:', JSON.stringify(err));
  });

  Push.addListener('pushNotificationReceived', (notification) => {
    console.log('[Push] Foreground notification:', notification);
    if (typeof onForegroundNotification === 'function') {
      onForegroundNotification({
        title: notification.title || 'New Notification',
        body:  notification.body  || '',
        data:  notification.data  || {},
      });
    }
  });

  Push.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[Push] Notification tapped:', action);
    const data = action.notification?.data || {};
    if (data.tab) {
      console.log('[Push] Navigate to tab:', data.tab);
    }
  });

  console.log('[Push] Listeners registered — now requesting permissions...');

  // ── NOW do the async permission + register calls ───────────────────────────
  try {
    const permStatus = await Push.requestPermissions();
    console.log('[Push] Permission status:', permStatus.receive);
    if (permStatus.receive !== 'granted') {
      console.warn('[Push] Permission not granted:', permStatus.receive);
      return;
    }

    console.log('[Push] Calling Push.register()...');
    await Push.register();
    console.log('[Push] Push.register() done — token will arrive via registration event');
  } catch (err) {
    console.error('[Push] Setup error:', err.message || err);
  }
}

/**
 * POST the device push token to the backend.
 */
async function savePushTokenToServer(deviceToken, leadToken, apiBaseUrl) {
  try {
    const base = apiBaseUrl || '';
    console.log('[Push] Saving token to server:', base);
    const response = await fetch(`${base}/api/lead-auth/push-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${leadToken}`,
      },
      body: JSON.stringify({
        token:    deviceToken,
        platform: window.Capacitor?.getPlatform() || 'unknown',
      }),
    });
    if (response.ok) {
      console.log('[Push] ✅ Device token saved to server');
    } else {
      const txt = await response.text();
      console.warn('[Push] Server rejected token:', response.status, txt);
    }
  } catch (err) {
    console.error('[Push] Failed to save token to server:', err.message || err);
  }
}

/**
 * Remove all listeners (call on logout).
 */
export function removePushListeners() {
  if (!window.Capacitor || !window.Capacitor.isNativePlatform()) return;
  const Push = getPushPlugin();  // sync — no await
  if (Push) {
    Push.removeAllListeners();
    console.log('[Push] Listeners removed');
  }
}
